import router from '@/router';
import store from '@/store';
import { encodeID } from './utils.js';

/**
 * Navigation helper functions for centralized routing
 * All functions directly push to router
 * Auto-detects public routes from current URL path
 * Validates authentication on protected routes
 */

// Helper function to get prefix based on current route
function getPrefix() {
  return router.currentRoute.value.path.includes('/public') ? '/public' : '';
}

// Helper function to check if current route is public
function isPublic() {
  return router.currentRoute.value.path.includes('/public');
}

// Helper function to check authentication and redirect to login if needed
function checkAuthAndRedirect() {
  const token = store.state.user?.token;
  const currentPathIsPublic = isPublic();
  
  // If no token and route is not public, redirect to login
  if (!token && !currentPathIsPublic) {
    router.push({ path: '/login' });
    return false;
  }
  return true;
}

// ======================== PROJECTS ========================
export function goto_projects() {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const path = `${prefix}/projects`;
  router.push({ path });
}

export function goto_project(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/projects/${encodedId}`;
  router.push({ path });
}

// ======================== EXPERIMENTS ========================
export function goto_experiments(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const path = `${prefix}/${projId}/experiments`;
  router.push({ path });
}

export function goto_experiment(projectId, experimentId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const expId = encodeID(experimentId, 'exp');
  const path = `${prefix}/${projId}/${expId}`;
  router.push({ path });
}

// ======================== EXPERIMENT TRAINERS ========================
export function goto_trainers(projectId, experimentId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const expId = encodeID(experimentId, 'exp');
  const path = `${prefix}/${projId}/${expId}/trainings`;
  router.push({ path });
}

export function goto_trainer_session(projectId, experimentId, sessionId, query = null) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const sessId = encodeID(sessionId, 't');
  const path = `${prefix}/training/${sessId}/details`;
  if (query) {
    router.push({ path, query });
  } else {
    router.push({ path });
  }
}

export function goto_training_charts(projectId, experimentId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const expId = encodeID(experimentId, 'exp');
  const path = `${prefix}/${projId}/${expId}/charts`;
  router.push({ path });
}

// ======================== EXPERIMENT VALIDATIONS ========================
export function goto_validations(projectId, experimentId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const expId = encodeID(experimentId, 'exp');
  const path = `${prefix}/${projId}/${expId}/validations`;
  router.push({ path });
}

export function goto_validation_session(projectId, experimentId, sessionId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const expId = encodeID(experimentId, 'exp');
  const sessId = encodeID(sessionId, 'v');
  const path = `${prefix}/${projId}/${expId}/${sessId}`;
  router.push({ path });
}

export function goto_validation_compare(projectId, experimentId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const projId = encodeID(projectId, 'p');
  const expId = encodeID(experimentId, 'exp');
  const path = `${prefix}/${projId}/${expId}/compare`;
  router.push({ path });
}

// ======================== DATASETS ========================
export function goto_datasets(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/${encodedId}/datasets`;
  router.push({ path });
}

export function goto_dataset_manager(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/${encodedId}/datasets`;
  router.push({ path });
}

export function goto_dataset_gallery(datasetId, projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const dsId = encodeID(datasetId, 'ds');
  const path = `${prefix}/datasets/${dsId}/gallery`;
  router.push({ path });
}

export function goto_dataset_gallery_viewer(datasetId, projectId, imageId = null, filters = {}) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const isCurrentRoutePublic = isPublic();
  const dsId = encodeID(datasetId, 'ds');
  
  // Support both new format (route params) and old format (query params)
  const hasNewFormat = !!router.currentRoute.value.params.datasetId;
  
  const query = {};
  if (imageId) {
    query.image_id = JSON.stringify(imageId);
  }
  if (filters.annotationDisplaySettings) {
    query.annotationDisplaySettings = JSON.stringify(filters.annotationDisplaySettings);
  }
  if (filters.imageDisplaySettings) {
    query.imageDisplaySettings = JSON.stringify(filters.imageDisplaySettings);
  }
  if (filters.showSequences !== undefined) {
    query.showSequences = String(filters.showSequences);
  }
  if (filters.filterParams) {
    query.filter_params = JSON.stringify(filters.filterParams);
  }
  if (filters.startAtFirstFrame) {
    query.start_at_first_frame = 'true';
  }
  
  if (hasNewFormat) {
    const path = `${prefix}/datasets/${dsId}/gallery/viewer`;
    router.push({ path, query });
  } else {
    const path = `${prefix}/datasets/${dsId}/gallery/viewer`;
    router.push({ path, query });
  }
}

export function goto_dataset_stats(datasetId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const dsId = encodeID(datasetId, 'ds');
  const path = `${prefix}/datasets/${dsId}/stats`;
  router.push({ path });
}

export function goto_dataset_map(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/${encodedId}/datasets/map`;
  router.push({ path });
}

// ======================== TASKBOARD ========================
export function goto_taskboard(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/${encodedId}/taskboard`;
  router.push({ path });
}

// ======================== ANNOTATOR ========================
export function goto_annotator(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/${encodedId}/annotator`;
  router.push({ path });
}

export function goto_annotation_editor(projectId) {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  const encodedId = encodeID(projectId, 'p');
  const path = `${prefix}/${encodedId}/annotator/editor`;
  router.push({ path });
}

// ======================== OTHER ROUTES ========================
export function goto_home() {
  if (isPublic()) {
    router.push({ path: '/' });
  } else {
    router.push({ path: '/home' });
  }
  
}

export function goto_user() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/user' });
}

export function goto_user_profile() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/user/profile' });
}

export function goto_user_edit(username) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/user/edit', query: { editUser: username } });
}

export function goto_admin() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/organization/details' });
}

export function goto_admin_editorg(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/organization/editorg', query: { org: orgId } });
}

export function goto_admin_security(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/organization/security', query: { org: orgId } });
}

export function goto_admin_bills(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/bills', query: { org: orgId } });
}

export function goto_admin_transactions(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/transactions', query: { org: orgId } });
}

export function goto_admin_people_list() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/people/list' });
}

export function goto_admin_people_newuser(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/people/newuser', query: { org: orgId } });
}

export function goto_admin_people_edituser() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/people/edituser' });
}

export function goto_admin_coupons() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/coupons' });
}

export function goto_admin_subscription() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/subscription' });
}

export function goto_admin_topup() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/admin/topup' });
}

export function goto_super_admin() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/list' });
}

export function goto_super_costreport() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/costreport' });
}

export function goto_super_neworg() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/neworg' });
}

export function goto_super_addcredits(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/addcredits', query: { org: orgId } });
}

export function goto_super_emulate() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/emulate' });
}

export function goto_super_coupons() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/coupons' });
}

export function goto_super_users(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/users', query: { org: orgId } });
}

export function goto_super_usage(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/usage', query: { org: orgId } });
}

export function goto_super_transactions(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/transactions', query: { org: orgId } });
}

export function goto_super_bills(orgId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/super/bills', query: { org: orgId } });
}

export function goto_recycle_bin() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/recycle' });
}

export function goto_apps(activityId) {
  if (!checkAuthAndRedirect()) return;
  if (activityId) {
    router.push({ path: '/apps', query: { activity_id: activityId } });
  } else {
    router.push({ path: '/apps' });
  }
}

export function goto_automation() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/automation' });
}

export function goto_tasks() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/tasks' });
}

export function goto_logs(taskId) {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: `/logs/${taskId}` });
}

export function goto_servers() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/servers' });
}

export function goto_snapshots() {
  if (!checkAuthAndRedirect()) return;
  router.push({ path: '/snapshots' });
}

export function goto_profiler_home() {
  if (!checkAuthAndRedirect()) return;
  const prefix = getPrefix();
  router.push({ path: `${prefix}/profilers` });
}

export function goto_profiler() {
  goto_profiler_home();
}

export function goto_login() {
  router.push({ path: '/login' });
}

export function goto_signup() {
  router.push({ path: '/signup' });
}

export function goto_logout() {
  router.push({ path: '/logout' });
}

export function goto_forgot_password() {
  router.push({ path: '/forgot-password' });
}

export function goto_price() {
  router.push({ path: '/price' });
}

export function goto_contact() {
  router.push({ path: '/contact' });
}

export function goto_public_projects() {
  router.push({ path: '/public/projects' });
}

export function goto_public_projects_signed() {
  router.push({ path: '/projects_public' });
}

// ======================== NAVIGATION REPLACE HELPER ========================
/**
 * Convenience function to replace route
 */
export function navigateReplace(routeObject) {
  return router.replace(routeObject);
}
